/**
 * Locale-aware navigation utilities.
 *
 * Provides helpers for navigating between localized routes.
 */

import { createNavigation } from 'next-intl/navigation';
import { ensureTrailingSlash } from '@/lib/utils/ensureTrailingSlash';
import { defaultLocale, locales } from './config';
import { createElement, forwardRef, type ComponentProps } from 'react';

const {
  Link: NextIntlLink,
  redirect,
  usePathname,
  useRouter,
} = createNavigation({
  locales,
  defaultLocale,
  localePrefix: 'always',
});

/**
 * Locale-aware Link that enforces a trailing slash on internal hrefs
 * so CMS-authored URLs never trigger a 301 redirect from the proxy.
 */
const Link = forwardRef<HTMLAnchorElement, ComponentProps<typeof NextIntlLink>>(function Link(
  { href, ...props },
  ref
) {
  let normalizedHref = href;
  if (typeof href === 'string') {
    normalizedHref = ensureTrailingSlash(href);
  } else if (typeof href === 'object' && href !== null && 'pathname' in href) {
    normalizedHref = {
      ...href,
      pathname: href.pathname ? ensureTrailingSlash(href.pathname) : href.pathname,
    };
  }
  return createElement(NextIntlLink, { ref, href: normalizedHref, ...props });
});

export { Link, redirect, usePathname, useRouter };

/**
 * Build a localized URL path.
 *
 * @example
 * buildLocalizedPath('/watches/all', 'de-de') // '/de-de/watches/all'
 */
export function buildLocalizedPath(path: string, locale: string): string {
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;

  // Remove any existing locale prefix
  const pathWithoutLocale = cleanPath.replace(/^[a-z]{2}-[a-z]{2,3}\/?/, '');

  return `/${locale}/${pathWithoutLocale}`;
}

/**
 * Get the path without the locale prefix.
 *
 * @example
 * stripLocaleFromPath('/us-en/watches/all') // '/watches/all'
 */
export function stripLocaleFromPath(path: string): string {
  // Match locale pattern at start of path
  const localePattern = /^\/[a-z]{2}-[a-z]{2,3}(\/|$)/;

  if (localePattern.test(path)) {
    return path.replace(localePattern, '/');
  }

  return path;
}
